'use client';

import { useReadingSize, ReadingSize } from '@/contexts/readingSizeContext';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface ReadingSizeSelectorProps {
  className?: string;
}

const sizes: { id: ReadingSize; label: string; fontSize: string; displayLabel: string }[] = [
  { id: 'small', label: 'A', fontSize: 'text-xs', displayLabel: 'Small' },
  { id: 'medium', label: 'A', fontSize: 'text-sm', displayLabel: 'Medium' },
  { id: 'large', label: 'A', fontSize: 'text-base', displayLabel: 'Large' },
];

export function ReadingSizeSelector({ className = '' }: ReadingSizeSelectorProps) {
  const { readingSize, setReadingSize } = useReadingSize();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentSize = sizes.find((s) => s.id === readingSize) || sizes[1];

  return (
    <>
      {/* Desktop View - Button Group */}
      <div
        className={cn(
          'hidden md:flex items-center gap-0.5 bg-[#0d1117] rounded-lg p-1 border border-slate-700/50',
          className
        )}
        role="radiogroup"
        aria-label="Reading size"
      >
        {sizes.map((size, index) => {
          const isActive = readingSize === size.id;
          return (
            <button
              key={size.id}
              onClick={() => setReadingSize(size.id)}
              role="radio"
              aria-checked={isActive}
              aria-label={`${size.id} text size`}
              className={cn(
                'relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 font-display font-semibold',
                size.fontSize,
                isActive
                  ? 'bg-gradient-to-br from-[#d4a853] to-[#b8923f] text-[#0d1117] shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              )}
            >
              {size.label}
              {index < sizes.length - 1 && !isActive && readingSize !== sizes[index + 1]?.id && (
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-3 bg-slate-700/50" />
              )}
            </button>
          );
        })}
      </div>

      {/* Mobile View - Touch-Friendly Dropdown */}
      <div className={cn('relative md:hidden', className)} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 bg-[#0d1117] rounded-lg px-3 py-2 border border-slate-700/50 hover:border-[#d4a853]/30 transition-all min-w-[100px]"
          aria-label="Select reading size"
          aria-expanded={isOpen}
        >
          <span className={cn('font-display font-semibold', currentSize.fontSize)}>
            {currentSize.label}
          </span>
          <span className="text-xs text-slate-400">{currentSize.displayLabel}</span>
          <ChevronDown
            size={14}
            className={cn(
              'text-slate-400 transition-transform ml-auto',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full right-0 mt-2 w-40 bg-[#1c2128] border border-[#30363d] rounded-lg shadow-lg overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            {sizes.map((size) => {
              const isActive = readingSize === size.id;
              return (
                <button
                  key={size.id}
                  onClick={() => {
                    setReadingSize(size.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-3 text-left transition-colors',
                    isActive
                      ? 'bg-[#d4a853]/10 text-[#d4a853]'
                      : 'text-slate-300 hover:bg-[#30363d]'
                  )}
                >
                  <span className={cn('font-display font-semibold', size.fontSize)}>
                    {size.label}
                  </span>
                  <span className="text-sm">{size.displayLabel}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
